// NotificationTileComponent - Individual notification display
// Phase 1 Implementation: UI Foundation & Models

import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ElementRef, OnInit, OnDestroy, Inject } from '@angular/core';
import { Notification, NotificationType } from '../../models/notification.model';
import { UserProfile } from "../../../../../common/src/lib/models/user-profile.model";
import { UsersService } from "../../services/users.service";
import { take } from "rxjs/operators";

@Component({
  selector: 'app-notification-tile',
  templateUrl: './notification-tile.component.html',
  styleUrls: ['./notification-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationTileComponent implements OnInit, OnDestroy {
  
  @Input() notification!: Notification;
  @Input() enableVisibilityTracking = true;
  @Output() delete = new EventEmitter<Event>();
  @Output() click = new EventEmitter<Notification>();
  @Output() visibilityChanged = new EventEmitter<{notification: Notification, isVisible: boolean}>();

  private intersectionObserver?: IntersectionObserver;
  private isCurrentlyVisible = false;
  private readMarkingTimeout?: number;

  // Icon mapping for notification types
  private readonly notificationIcons: Record<NotificationType, string> = {
    system: 'settings',
    info: 'info',
    success: 'check_circle',
    warning: 'warning',
    error: 'error'
  };

  // Color mapping for notification types
  private readonly notificationColors: Record<NotificationType, string> = {
    system: 'accent',
    info: 'primary',
    success: 'success',
    warning: 'warn',
    error: 'error'
  };

  // Get the appropriate icon for notification type
  getNotificationIcon(): string {
    return this.notificationIcons[this.notification.type] || 'notifications';
  }

  // Get the appropriate color for notification type
  getNotificationColor(): string {
    return this.notificationColors[this.notification.type] || 'primary';
  }

  // Check if notification is unread
  isUnread(): boolean {
    return !this.notification.readAt;
  }

  // Check if notification has an action URL
  hasActionUrl(): boolean {
    return !!this.notification.actionUrl;
  }

  // Get human-readable time ago string
  getTimeAgo(): string {
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - this.notification.createdAt.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;

    // For older notifications, show the date
    return this.notification.createdAt.toLocaleDateString();
  }


  // Handle notification click (for future actions)
  onNotificationClick(): void {
    // If there's a pending read marking timeout, cancel it and mark as read immediately
    if (this.readMarkingTimeout && this.isUnread()) {
      clearTimeout(this.readMarkingTimeout);
      this.readMarkingTimeout = undefined;
      
      // Immediately mark as read via visibility changed event
      this.visibilityChanged.emit({
        notification: this.notification,
        isVisible: true
      });
    }
    
    this.click.emit(this.notification);
  }

  // Handle delete button click
  onDelete(event: Event): void {
    event.stopPropagation(); // Prevent triggering the main click
    this.delete.emit(event);
  }

  // Get truncated message for display
  getTruncatedMessage(maxLength: number = 120): string {
    if (this.notification.message.length <= maxLength) {
      return this.notification.message;
    }
    return this.notification.message.substring(0, maxLength) + '...';
  }

  // Get notification type display label
  getTypeLabel(): string {
    const labels: Record<NotificationType, string> = {
      system: 'System',
      info: 'Info',
      success: 'Success',
      warning: 'Warning',
      error: 'Error'
    };
    return labels[this.notification.type] || 'Notification';
  }

  fromUser?: UserProfile;

  // Get CSS classes for the tile based on notification state
  getTileClasses(): string {
    const classes = ['notification-tile'];
    
    if (this.isUnread()) {
      classes.push('unread');
    }
    
    if (this.hasActionUrl()) {
      classes.push('clickable');
    }
    
    return classes.join(' ');
  }

  constructor(
      private elementRef: ElementRef,
      private usersService: UsersService
  ) {}

  async ngOnInit() {
    if (this.enableVisibilityTracking && 'IntersectionObserver' in window) {
      this.setupVisibilityTracking();
    }

    if(this.notification.data?.fromUser) {
      const users = (await this.usersService.users$.pipe(take(1)).toPromise())!;
      this.fromUser = users.find(user => user.id === this.notification.data!.fromUser);
    }
  }

  ngOnDestroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.readMarkingTimeout) {
      clearTimeout(this.readMarkingTimeout);
    }
  }

  private setupVisibilityTracking(): void {
    // Configure observer to trigger when element is fully visible
    const options: IntersectionObserverInit = {
      root: null, // Use viewport as root
      rootMargin: '0px',
      threshold: 1.0 // Element must be 100% visible
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const isVisible = entry.isIntersecting && entry.intersectionRatio >= 1.0;
        
        // Only emit if visibility state changed
        if (isVisible !== this.isCurrentlyVisible) {
          this.isCurrentlyVisible = isVisible;
          
          if (isVisible && this.isUnread()) {
            // Start timer to mark as read after 3 seconds of being visible
            this.readMarkingTimeout = window.setTimeout(() => {
              this.visibilityChanged.emit({
                notification: this.notification,
                isVisible: true
              });
            }, 2000);
          } else if (!isVisible && this.readMarkingTimeout) {
            // Cancel timer if notification goes out of view before 3 seconds
            clearTimeout(this.readMarkingTimeout);
            this.readMarkingTimeout = undefined;
          }
        }
      });
    }, options);

    // Start observing this element
    this.intersectionObserver.observe(this.elementRef.nativeElement);
  }
}